<template>
    <div class="tw-flex tw-bg-white tw-h-full tw-p-4 questions">
        <div class="left-bar tw-flex tw-flex-col">
            <h5 class="tw-text-base"><b>Pages</b> <span v-if="pages.length">({{ pages.length }})</span></h5>
            <draggable v-model="pageIds" class="pages-list tw-overflow-auto">
                <div class="page tw-p-2 tw-flex tw-justify-between tw-items-center tw-group"
                    v-for="page in pages" :key="page.id"
                    :class="{ 'active': page.id === selectedPage.id }"
                    :data-icon="page.question_type"
                    @click="selectPage(page)">
                    <span>{{ page.name }}</span>
                    <span v-if="displayMode === 'editor'" class="ws-button ws-button-secondary edit tw-cursor-pointer"
                        @click="onEditPage(page)">
                    </span>
                </div>
            </draggable>
            <div v-if="displayMode === 'editor'" class="tw-mt-auto tw-text-center">
                <modal v-if="showEditModal" @close="showEditModal = false" >
                    <page-settings-form :page="selectedPage" @save="onUpdate" @delete="onDelete" @duplicate="onDuplicate"
                        @close="showEditModal = false" />
                </modal>
                <modal v-if="showCreateModal" @close="showCreateModal = false" :width="750" >
                    <new-page-form @save="onCreate" />
                </modal>
                <div style="font-size: 12px; line-height: 17px; text-align: left; margin: 10px 0; opacity: 0.6;">
                    <b>TIP:</b> Drag and drop pages above to rearrange them in any order.
                </div>
                <div class="ws-button ws-button-main add-new tw-w-full" @click="showCreateModal = true">Add New Page</div>
            </div>
        </div>
        
        <div class="center-bar tw-rounded-lg">
            <div class="inner tw-h-full" v-if="selectedPage.id">
                <page-view
                           :page="selectedPage"
                           :styles="selectedTheme.styles"
                           :editable="displayMode === 'editor'"
                           :readonly="displayMode !== 'editor'"
                           view="editor"
                />
            </div>

            <div v-if="!pages.length"
                 class="tw-p-2 tw-h-full tw-text-center tw-flex tw-justify-center tw-items-center">
                <p>Your survey doesn't have any pages.<span class="ws-button ws-button-main add-new" style="display: block; margin-top: 10px" @click="showCreateModal = true">Add Your First Page</span> </p>
            </div>
            <div v-else-if="!selectedPage.id"
                 class="tw-p-2 tw-h-full tw-text-center tw-flex tw-justify-center tw-items-center">
                <p>Select a page to start editing.</p>
            </div>
            <div class="modes tw-flex tw-gap-2">
                <div class="ws-button tw-flex-1 tw-text-center" :class="{ 'active': displayMode === 'editor' }"
                     @click="displayMode = 'editor'">Editor Mode
                </div>
                <div class="ws-button tw-flex-1 tw-text-center" :class="{ 'active': displayMode === 'preview' }"
                     @click="displayMode = 'preview'">Preview Mode
                </div>
            </div>
        </div>
        <survey-settings-bar :parent="survey" />
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import Modal from '@/components/modals/Modal';
import PageSettingsForm from "@/pages/surveys/PageSettingsForm";
import PageView from "@/pages/surveys/PageView";
import NewPageForm from "@/pages/surveys/NewPageForm.vue";
import SurveySettingsBar from "@/pages/surveys/SurveySettingsBar.vue";

export default {
    name: 'SurveyDetailsQuestions',
    components: {SurveySettingsBar, PageView, NewPageForm, PageSettingsForm, Modal, draggable },
    data() {
        return {
            showCreateModal: false,
            showEditModal: false,
            displayMode: 'editor',
            addNewPageWidth: 750,
        }
    },
    computed: {
        samplePage() {
            return this.$store.state.surveyThemeSamplePageSmall;
        },
        pageIds: {
            get() {
                return this.$store.state.surveyPages.ids;
            },
            async set(ids) {
                await this.$store.dispatch('surveyPages/reorder', ids.filter(id => id));
                await this.fetchSurvey();
            },
        },
        survey() {
            return this.$store.getters["surveys/selected"];
        },
        selectedPage() {
            return this.$store.getters["surveyPages/selected"] || {};
        },
        pages() {
            return this.$store.getters["surveyPages/orderedList"];
        },
        questions() {
            return this.$store.getters["SurveyQuestions/orderedList"];
        },
        themes() {
            return this.$store.getters["surveyThemes/orderedList"];
        },
        selectedTheme() {
            if (!this.survey.theme_id) {
                return {}
            }
            return this.$store.state.surveyThemes.list[this.survey.theme_id] || {};
        },
    },
    beforeRouteUpdate(to) {
        this.fetchSurvey(to.params.survey_id);
    },
    created() {
        this.$store.dispatch('surveyPages/reset');
        this.fetchSurvey(this.$route.params.survey_id);
    },
    methods: {
        async fetchSurvey() {
            await this.$store.dispatch('surveyPages/getList', {})
            if (this.pages.length) {
                await this.selectPage(this.pages[0])
            }
            await this.$store.dispatch('surveyThemes/getList', { active: true })
        },
        async selectPage(page) {
            this.$store.commit('surveyPages/setSelected', page.id);
            page = await this.$store.dispatch('surveyPages/getById', page.id);

            this.$store.commit('surveyQuestions/setList', page.questions);
            if (this.selectedPage.questions.length) this.$store.commit('surveyQuestions/setSelected', this.selectedPage.questions[0].id);
            else this.$store.commit('surveyQuestions/setSelected', null);
        },
        async onCreate(data) {
            const page = await this.$store.dispatch('surveyPages/create', data);
            await this.selectPage(page);
            this.showCreateModal = false;
        },
        async onEditPage(page) {
            await this.selectPage(page)
            this.showEditModal = true;
        },
        async onUpdate(data) {
            const page = await this.$store.dispatch('surveyPages/update', data);
            await this.fetchSurvey();
            await this.selectPage(page);
            this.showEditModal = false;
        },
        async onDelete(id) {
            await this.$store.dispatch('surveyPages/delete', id);
            await this.fetchSurvey();
            if (this.pages.length) await this.selectPage(this.pages[0]);
            this.showEditModal = false;
        },
        async onDuplicate(data) {
            const page = await this.$store.dispatch('surveyPages/create', data);
            await this.selectPage(page);
            this.showEditModal = false;
        },
        onNewPageFormClose() {
            this.showCreateModal = false;
        }

    },
}
</script>
